import { Avatar, firstNameOf, Tooltip } from "@baton/ui";
import type { ButtonHTMLAttributes, Ref } from "react";
import type { MeView } from "../../app/queries.ts";
import { AccountCard, actorOf } from "../sidebar/AccountCard.tsx";

/**
 * The word under the avatar: the person's first name (`firstNameOf`), or, for a name with no words,
 * the local part of their email, so the tile never shows an empty line. 001 trims a display name and
 * holds it to 1 to 80 characters, so the fallback covers a name that reached the client blank.
 */
export function railNameOf(me: Pick<MeView, "display_name" | "email">): string {
  const first = firstNameOf(me.display_name);
  return first === "" ? (me.email.split("@")[0] ?? me.email) : first;
}

interface TileProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  readonly me: MeView;
  readonly label: string;
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * The tile the account card's trigger slot receives: the popover's trigger props land on the button
 * through the spread, beneath the tooltip's own trigger, as `IconButton` composes the two.
 */
function AccountTile({ me, label, ref, ...rest }: TileProps) {
  return (
    <Tooltip content={me.display_name} placement="right">
      <button ref={ref} type="button" className="b-rail__item b-rail__item--account" data-glide-row aria-label={label} {...rest}>
        <Avatar actor={actorOf(me)} size="lg" />
        <span className="b-rail__label" aria-hidden="true">
          {railNameOf(me)}
        </span>
      </button>
    </Tooltip>
  );
}

/**
 * The rail's avatar tile (2026-09-17): the one place the account card opens from, at the rail's
 * bottom on every screen; the sidebar carries no actor card of its own. Since 2026-09-29 (the
 * owner's review of the live site: "it should say the first name of the user under the avatar
 * no?") it is the other tiles' shape, the avatar over the person's first name, cut with an ellipsis
 * when long; the full name is the tooltip and is in the accessible name, "Account menu for" the
 * full name, which holds the visible word (SC 2.5.3).
 */
export function RailAccount({ me, outsideTeam = false }: { readonly me: MeView; readonly outsideTeam?: boolean }) {
  return <AccountCard me={me} placement="right" align="end" outsideTeam={outsideTeam} trigger={(label) => <AccountTile me={me} label={label} />} />;
}
